// Canonical list of badge types surfaced in the UI. Two consumers:
//   - CreateBadgePage wizard (with an icon zipped in via ICON_BY_TYPE)
//   - AvailableBadgesPage type filter dropdown
// Keeping this list in one place so the two can't drift.

export type BadgeType = 'read' | 'watch' | 'geo' | 'instant' | 'manual' | 'multi';

export interface BadgeTypeOption {
    type: BadgeType;
    label: string;
    description: string;
}

export const BADGE_TYPES: BadgeTypeOption[] = [
    { type: 'read',    label: 'Read-to-Earn',   description: 'Earn by reading a webpage' },
    { type: 'watch',   label: 'Watch-to-Earn',  description: 'Earn by watching a video' },
    { type: 'geo',     label: 'Find-to-Earn',   description: 'Earn by visiting a location' },
    { type: 'instant', label: 'Earn Now',       description: 'Immediately earnable badge' },
    { type: 'manual',  label: 'Recognition',    description: 'Manually issued recognition badge' },
    { type: 'multi',   label: 'Multi Badge',    description: 'Earn by completing multiple badges' },
];

/**
 * Map a stored `Badge.badgeType` to the wizard's BadgeType.
 *
 * The DB (Pages.Type) uses legacy synonyms — the live distribution is
 * visit-scroll / manual / watch / learningpath / augmentreality / Default — while the
 * UI uses the BadgeType union above. Server-side the same pairing lives in
 * BADGE_TYPE_SYNONYMS (server/src/services/open-badges.service.ts); keep the two in step.
 *
 * `augmentreality` (the AR/Exploration badges created by the mobile app) maps to nothing
 * in the wizard, so it returns null rather than being force-fitted to a type it isn't.
 */
export function dbBadgeTypeToWizard(input: string | null | undefined): BadgeType | null {
    switch (input) {
        case 'visit-scroll':
        case 'read':         return 'read';
        case 'watch':        return 'watch';
        case 'geo':          return 'geo';
        case 'learningpath':
        case 'multi':        return 'multi';
        case 'manual':       return 'manual';
        case 'Default':
        case 'instant':      return 'instant';
        default:             return null;
    }
}

/**
 * True only for Multi Badges (stored as 'learningpath'). This is the sole condition for
 * drawing a badge as a stack of cards — a Multi Badge is literally several badges, so the
 * stacked frame carries meaning. Every other type renders as a single card.
 */
export function isMultiBadge(badgeType: string | null | undefined): boolean {
    return dbBadgeTypeToWizard(badgeType) === 'multi';
}
